import identity from 'lodash/identity';
import React, { useState } from 'react';
import TagManager from 'react-gtm-module';
import type { TagManagerArgs } from 'react-gtm-module';

import type { Country } from '@skippay/core/@types/country';

import { GA4_PAGEVIEW, GA_PAGEVIEW } from './constants';
import GoogleAnalyticsContext, { Context } from './context';
import { enhanceEvent, enhancePageview, pushNonEmptyToDataLayer } from './global';
import { GA4Event, GA4Pageview, GAEvent, GAPageview } from './types';

function isThenable<T extends Record<string, unknown>>(obj: T | Promise<T>): obj is Promise<T> {
	return typeof obj.then === `function`;
}

const isPageview = (
	obj: GAEvent | GA4Event | GAPageview | GA4Pageview
): obj is GAPageview | GA4Pageview => obj.event === GA_PAGEVIEW || obj.event === GA4_PAGEVIEW;

const AnalyticsProvider: React.FC<Props> = ({
	children,
	options,
	mapObject = identity,
	onPageview,
	country,
}) => {
	const [context] = useState<Context>(() => {
		TagManager.initialize(options);
		const pushAndNotify = (mapped: GAEvent | GA4Event | GAPageview | GA4Pageview) => {
			pushNonEmptyToDataLayer(mapped);
			if (mapped && onPageview && isPageview(mapped)) {
				try {
					onPageview(mapped);
				} catch {
					// pageview observers must never break analytics
				}
			}
		};
		const mapAndPush = (obj: GAEvent | GA4Event | GAPageview | GA4Pageview) => {
			const mapped = mapObject(obj);
			if (isThenable(mapped)) {
				mapped.then(pushAndNotify);
			} else {
				pushAndNotify(mapped);
			}
		};
		const sendEvent: Context['sendEvent'] = (eventData, eventExtra) =>
			mapAndPush(enhanceEvent(eventData, { ...eventExtra, country }));
		const sendPageview: Context['sendPageview'] = (pageview, previousLocation) =>
			mapAndPush(enhancePageview({ ...pageview, country }, previousLocation));
		return { sendEvent, sendPageview };
	});
	return (
		<GoogleAnalyticsContext.Provider value={context}>{children}</GoogleAnalyticsContext.Provider>
	);
};

export type Props = {
	children: React.ReactNode;
	options: TagManagerArgs;
	mapObject?<T extends GAEvent | GA4Event | GAPageview | GA4Pageview>(object: T): T | Promise<T>;
	onPageview?(pageview: GAPageview | GA4Pageview): void;
	country: Country;
};

export default AnalyticsProvider;
